import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { CalendarOff, PartyPopper, Plane } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { fetchAdminTimeOffList } from "@/lib/api/admin-time-off";
import { subscribeAppointmentRefresh } from "@/lib/realtime/appointment-realtime-bridge";
import { isCalendarColumnId } from "@/lib/calendars";

function asTrimmedString(value) {
  return typeof value === "string" ? value.trim() : "";
}

function toDateKey(date) {
  if (!date) return "";
  if (typeof date.toFormat === "function") return date.toFormat("yyyy-LL-dd");
  if (date instanceof Date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  return String(date || "");
}

/**
 * Map an API time-off item to the shape used throughout the visualization layer.
 * The dates are kept in their original timezone (from `time.timezone`) so all-day
 * blocks render on the calendar's local day even across DST.
 */
function mapApiBlock(item) {
  if (!item) return null;
  const id = asTrimmedString(item.id);
  if (!id) return null;
  const tz = asTrimmedString(item?.time?.timezone) || "UTC";
  const startStr = asTrimmedString(item?.time?.start);
  const endStr = asTrimmedString(item?.time?.end);
  const startAt = DateTime.fromISO(startStr, { zone: tz });
  const endAt = DateTime.fromISO(endStr, { zone: tz });
  if (!startAt.isValid || !endAt.isValid) return null;
  return {
    id,
    scope: asTrimmedString(item.scope) || "doctor",
    type: asTrimmedString(item.type) || "block",
    source: asTrimmedString(item.source) || "custom",
    title: asTrimmedString(item.title) || "(untitled)",
    description: asTrimmedString(item.description) || undefined,
    allDay: Boolean(item.allDay),
    ownerId: asTrimmedString(item.ownerId) || undefined,
    relatedDoctorId: asTrimmedString(item.relatedDoctorId) || undefined,
    tenantId: asTrimmedString(item.tenantId) || undefined,
    startAt,
    endAt,
    timezone: tz,
  };
}

/**
 * Hook: fetch admin time-off blocks for one or more tenants over a date range.
 *
 * Errors (including 401/403 for non-admin viewers) are silenced — the hook simply
 * returns an empty array. The calendar continues to render normally; only the
 * block annotations are missing for users who can't read the admin endpoint.
 */
export function useTimeOffBlocks({ fromDate, toDate, refreshKey = 0 } = {}) {
  const [blocks, setBlocks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [bridgeVersion, setBridgeVersion] = useState(0);

  useEffect(() => subscribeAppointmentRefresh(({ version }) => setBridgeVersion(version)), []);

  const rangeKey = useMemo(() => {
    const f = fromDate?.toUTC?.()?.toISO?.() || "";
    const t = toDate?.toUTC?.()?.toISO?.() || "";
    return `${f}|${t}|${refreshKey}|${bridgeVersion}`;
  }, [fromDate, toDate, refreshKey, bridgeVersion]);

  useEffect(() => {
    if (!fromDate || !toDate) {
      setBlocks([]);
      return undefined;
    }
    if (!fromDate.isValid || !toDate.isValid || toDate <= fromDate) {
      setBlocks([]);
      return undefined;
    }
    let cancelled = false;
    setIsLoading(true);
    fetchAdminTimeOffList({
      from: fromDate.toUTC().toISO(),
      to: toDate.toUTC().toISO(),
      limit: 1000,
    })
      // Swallow errors — non-admins (401/403) get an empty list and the calendar still works.
      .catch(() => ({ items: [] }))
      .then((response) => {
        if (cancelled) return;
        const items = Array.isArray(response?.items) ? response.items : [];
        const merged = [];
        for (const item of items) {
          const mapped = mapApiBlock(item);
          if (mapped) merged.push(mapped);
        }
        setBlocks(merged);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey]);

  return { blocks, isLoading };
}

/**
 * Returns blocks that intersect a given local date. The match is computed in the
 * block's own timezone so an all-day Christmas Day stays on Dec 25 regardless of
 * viewer tz — the block.startAt date-key compared against the target date-key.
 */
export function blocksForDate(blocks, date) {
  if (!Array.isArray(blocks) || blocks.length === 0) return [];
  if (!date) return [];
  const targetKey = toDateKey(date);
  return blocks.filter((block) => {
    const start = block.startAt;
    const end = block.endAt;
    if (!start || !end) return false;
    const startKey = start.toFormat ? start.toFormat("yyyy-LL-dd") : "";
    // For "all day on the last day", end is exclusive midnight of the next day; subtracting 1ms
    // gives the last actual moment of the previous day so we use the right date-key.
    const endInclusive =
      end.minus && +end > +start ? end.minus({ milliseconds: 1 }) : end;
    const endKey = endInclusive?.toFormat ? endInclusive.toFormat("yyyy-LL-dd") : "";
    return startKey <= targetKey && targetKey <= endKey;
  });
}

/**
 * Returns blocks applicable to a particular doctor on a particular date. Global
 * blocks always apply; per-doctor blocks apply only when ownerId or relatedDoctorId
 * matches. Pass `doctorId=null` to return only global blocks.
 */
export function blocksForDoctorOnDate(blocks, date, doctorId) {
  const dayBlocks = blocksForDate(blocks, date);
  if (!doctorId) return dayBlocks.filter((b) => b.scope === "global");
  return dayBlocks.filter((b) => {
    if (b.scope === "global") return true;
    if (b.ownerId === doctorId) return true;
    if (b.relatedDoctorId === doctorId) return true;
    return false;
  });
}

/**
 * Blocks relevant to one rendered day-view column. Only doctor-grouped, titled
 * columns get per-doctor scoping (a doctor's vacation shows only in their own
 * column); tenant grouping and the merged "_all" column keep showing every block
 * for the date, unchanged. A standalone-calendar column ("cal:<id>", kind
 * "calendar") belongs to no doctor, so it only shows clinic-wide (global) closures.
 */
export function blocksForColumn(blocks, date, { splitColumns, groupBy, columnId, kind } = {}) {
  if (splitColumns && (kind === "calendar" || isCalendarColumnId(columnId))) {
    return blocksForDoctorOnDate(blocks, date, null);
  }
  if (splitColumns && groupBy === "doctor" && columnId && columnId !== "_all") {
    return blocksForDoctorOnDate(blocks, date, columnId);
  }
  return blocksForDate(blocks, date);
}

/**
 * Whether a block closes the whole day and should paint the full-column reserved
 * hatch (as opposed to a partial-time block, which only shows as a chip).
 */
export function isDayClosingBlock(block) {
  return Boolean(block) && (block.allDay || block.type === "holiday" || block.type === "vacation");
}

/**
 * Theme tokens for a block (icon + color classes + stripe style). Color is paired
 * with an icon and stripe pattern so the block reads correctly without color (a11y).
 */
export function blockTheme(block) {
  if (block?.type === "holiday") {
    return {
      Icon: PartyPopper,
      label: "Holiday",
      iconClass: "text-rose-600 dark:text-rose-300",
      tintClass: "bg-rose-50/70 dark:bg-rose-950/30",
      borderClass: "border-rose-300/70 dark:border-rose-500/40",
      cellTintClass: "bg-rose-50/40 dark:bg-rose-950/30",
      stripeStyle: {
        backgroundImage:
          "repeating-linear-gradient(45deg, transparent 0 8px, rgba(244, 63, 94, 0.10) 8px 9px)",
      },
    };
  }
  if (block?.type === "vacation") {
    return {
      Icon: Plane,
      label: "Vacation",
      iconClass: "text-amber-600 dark:text-amber-300",
      tintClass: "bg-amber-50/70 dark:bg-amber-950/30",
      borderClass: "border-amber-300/70 dark:border-amber-500/40",
      cellTintClass: "bg-amber-50/40 dark:bg-amber-950/30",
      stripeStyle: {
        backgroundImage:
          "repeating-linear-gradient(45deg, transparent 0 8px, rgba(245, 158, 11, 0.10) 8px 9px)",
      },
    };
  }
  return {
    Icon: CalendarOff,
    label: "Time off",
    iconClass: "text-slate-500 dark:text-slate-300",
    tintClass: "bg-slate-100/70 dark:bg-slate-900/40",
    borderClass: "border-slate-300/70 dark:border-slate-500/40",
    cellTintClass: "bg-slate-100/50 dark:bg-slate-900/40",
    stripeStyle: {
      backgroundImage:
        "repeating-linear-gradient(45deg, transparent 0 8px, rgba(100, 116, 139, 0.10) 8px 9px)",
    },
  };
}

/**
 * Compact pill rendering of a block — shown above day/week columns and inside month cells.
 */
export function BlockChip({ block, doctorName, className }) {
  if (!block) return null;
  const theme = blockTheme(block);
  const Icon = theme.Icon;
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium leading-tight",
        theme.tintClass,
        theme.borderClass,
        className,
      )}
      title={block.description || block.title}
    >
      <Icon className={cn("size-3 shrink-0", theme.iconClass)} aria-hidden />
      <span className="truncate">{block.title}</span>
      {doctorName ? (
        <span className="truncate text-muted-foreground">· {doctorName}</span>
      ) : null}
      {block.scope === "global" ? (
        <Badge variant="secondary" className="ml-0.5 px-1 py-0 text-[9px] uppercase tracking-wide">
          Global
        </Badge>
      ) : null}
    </span>
  );
}

/**
 * Absolute-positioned overlay for a column or cell. Pointer-events:none so the
 * underlying appointments and slots remain interactive through the overlay.
 *
 * The first matching block dictates the stripe color; if blocks of multiple types
 * overlap, the strongest (holiday > vacation > generic) wins for the tint.
 */
export function BlockOverlay({ blocks, className, style }) {
  if (!Array.isArray(blocks) || blocks.length === 0) return null;
  const dominant =
    blocks.find((b) => b.type === "holiday") ||
    blocks.find((b) => b.type === "vacation") ||
    blocks[0];
  const theme = blockTheme(dominant);
  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-0 z-10 mix-blend-multiply dark:mix-blend-screen",
        theme.tintClass,
        className,
      )}
      style={{ ...theme.stripeStyle, ...style }}
    />
  );
}
